/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ResetPasswordModal from "@monsma/App/Components/ResetPasswordModal/ResetPasswordModal";
import { screen } from "@monsma/XDL/Helpers";
import { mergeWithCss } from "@monsma/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    extendedStyles?: SignInStyles;
    changePasswordInstructions?: string;
    fullWidthButton?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
    };
};

const mapDispatchToProps = {
    signIn,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface SignInStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    forgotPasswordLink?: LinkPresentationProps;
    changePasswordModal?: ModalPresentationProps;
}

export const signInStyles: SignInStyles = {
    signInGridContainer: { gap: 20 },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            position: relative;
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    rememberMeGridItem: {
        width: 6,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 10,
            weight: 700,
            lineHeight: "16px",
        },
        css: css`
            position: absolute;
            right: 10px;
            top: 12px;
        `,
    },
    signInButton: {
        css: css`
            ${screen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

const StyledForm = getStyledWrapper("form");

const SignIn = ({
    changePasswordInstructions,
    externalError,
    signIn,
    accountSettings,
    extendedStyles,
    returnUrl,
    isConfirmationPage,
    homePageUrl,
    theme,
    fullWidthButton,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(signInStyles, extendedStyles));
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

    const openResetPassword = () => {
        setIsResetPasswordModalOpen(true);
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();

    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;

        setIsSignInClicked(true);

        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.signInGridContainer}>
                <GridItem {...styles.userNameGridItem}>
                    <TextField
                        id="userName"
                        {...styles.userNameTextField}
                        label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                        onChange={e => setUserName(e.currentTarget.value)}
                        value={userName}
                        error={userErrorMessage}
                        autoComplete="username"
                        data-test-selector="signIn_userName"
                    />
                </GridItem>
                <GridItem {...styles.passwordGridItem}>
                    <TextField
                        id="password"
                        {...styles.passwordTextField}
                        label={translate("Password")}
                        onChange={e => setPassword(e.currentTarget.value)}
                        value={password}
                        error={passwordErrorMessage}
                        iconProps={{
                            ...styles.icon,
                            src: showPassword ? EyeOff : Eye,
                        }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowPassword(!showPassword);
                            },
                            type: "button",
                        }}
                        clickableText={password}
                        autoComplete="current-password"
                        data-test-selector="signIn_password"
                        type={showPassword ? "text" : "password"}
                    />
                    <Link
                        {...styles.forgotPasswordLink}
                        type="button"
                        onClick={openResetPassword}
                        data-test-selector="signIn_forgotPassword"
                    >
                        {translate("Forgot Password?")}
                    </Link>
                    <ResetPasswordModal
                        isResetPasswordModalOpen={isResetPasswordModalOpen}
                        onResetPasswordClose={onResetPasswordClose}
                        data-test-selector="resetPasswordModal"
                    />
                </GridItem>
                <GridItem {...styles.rememberMeGridItem}>
                    {showRememberMe && (
                        <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                            <Checkbox
                                uid="rememberMe"
                                {...styles.rememberMeCheckbox}
                                checked={rememberMe}
                                onChange={(_, value) => setRememberMe(value)}
                            >
                                {translate("Remember Me")}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                    {showKeepMeSignedIn && (
                        <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                            <Checkbox
                                uid="keepMeSignedIn"
                                {...styles.rememberMeCheckbox}
                                checked={keepMeSignedIn}
                                onChange={(_, value) => setKeepMeSignedIn(value)}
                            >
                                {translate("Keep Me Signed In")}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                </GridItem>
                <GridItem {...styles.signInButtonGridItem}>
                    <Button
                        {...styles.signInButton}
                        type="submit"
                        data-test-selector="signIn_submit"
                    >
                        {translate("Sign In")}
                    </Button>
                    <Modal
                        headline={translate("Change Password")}
                        {...styles.changePasswordModal}
                        isOpen={isChangePasswordModalOpen}
                        handleClose={onChangePasswordClose}
                    >
                        <SignInChangePasswordForm
                            enteredUserName={userName}
                            enteredPassword={password}
                            instructionsText={changePasswordInstructions}
                            onPasswordChanged={onPasswordChanged}
                        />
                    </Modal>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(SignIn));
